import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { buildImageUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import { extractBadges } from "@/lib/badges";

const AVAIL_STYLES = {
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Out of Stock": "bg-rose-50 text-rose-700 border-rose-200",
  "On Request": "bg-amber-50 text-amber-700 border-amber-200",
};

export const ProductCard = ({ product, showSpecs = false }) => {
  const { currency, settings, tr, lang } = useApp();
  const specBadges = showSpecs && product.type === "phone" ? extractBadges(product, 4) : [];
  const img = product.images?.[0];
  const displayName = lang === "ar" && product.name_ar ? product.name_ar : product.name;
  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.product(product.name))}`;

  return (
    <div className="card-lift bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block bg-slate-50 aspect-square flex items-center justify-center p-6 overflow-hidden">
        {img ? (
          <img src={buildImageUrl(img)} alt={displayName} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 hover:scale-105" />
        ) : (
          <div className="text-slate-300 text-sm">No image</div>
        )}
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{product.brand}</p>
            <h3 className="text-base font-semibold text-[#0B132B] truncate">{displayName}</h3>
          </div>
          <span className={`shrink-0 text-[10px] px-2 py-1 rounded-full border ${AVAIL_STYLES[product.availability] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
            {tr.availability[product.availability] || product.availability}
          </span>
        </div>

        {specBadges.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`spec-badges-${product.id}`}>
            {specBadges.map((b) => (
              <span
                key={b}
                dir="ltr"
                className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#1E5FD9]/10 text-[#1E5FD9] dark:text-[#7FA8FF] text-[11px] font-semibold border border-[#1E5FD9]/25 whitespace-nowrap"
                data-testid={`spec-badge-${product.id}-${b.replace(/\s/g, "")}`}
              >
                {b}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 text-xs text-slate-500">
          {product.storage_options?.slice(0, 2).map((s) => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100">{s}</span>
          ))}
          {product.colors?.[0] && <span className="px-2 py-0.5 rounded-full bg-slate-100">{product.colors[0]}</span>}
        </div>

        <div className="mt-auto flex items-center justify-between gap-2">
          <div className="text-lg font-bold text-[#1E5FD9] font-display" data-testid={`price-${product.id}`}>
            {formatPrice(product.price, currency, settings)}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button asChild className="w-full rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]">
            <Link to={`/request-quote?product=${encodeURIComponent(product.name)}&id=${product.id}`} data-testid={`quote-${product.id}`}>
              {tr.common.requestQuote}
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="flex-1 min-w-0 rounded-full border-slate-300 hover:border-[#1E5FD9] hover:text-[#1E5FD9]">
              <Link to={`/product/${product.id}`} data-testid={`view-details-${product.id}`} className="truncate">
                <span className="truncate">{tr.common.viewDetails}</span>
              </Link>
            </Button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center justify-center px-3.5 rounded-full bg-[#25D366] hover:bg-[#1EBE57] text-white transition-colors"
              aria-label="WhatsApp"
              data-testid={`whatsapp-${product.id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
